import { useState, useEffect } from 'react';
import { coreAPI } from '../../lib/api';
import type { UserProfile } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { DEPARTMENTS, departmentLabel } from '../../lib/departments';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/** Select value for a blank department (Radix rejects an empty-string item). */
const NO_DEPARTMENT = 'none';
/** Prefix for a stored value outside DEPARTMENTS, kept so it is not dropped. */
const LEGACY_PREFIX = 'legacy:';

function toSelectValue(stored: string | null | undefined): string {
  const raw = (stored ?? '').trim();
  if (!raw) return NO_DEPARTMENT;
  const key = raw.toLowerCase();
  return DEPARTMENTS.some((d) => d.value === key) ? key : `${LEGACY_PREFIX}${raw}`;
}

function toStoredValue(selectValue: string): string {
  if (selectValue === NO_DEPARTMENT) return '';
  if (selectValue.startsWith(LEGACY_PREFIX)) return selectValue.slice(LEGACY_PREFIX.length);
  return selectValue;
}

interface UserDepartmentFieldProps {
  /** The customer-company user whose department is edited. */
  selectedUser: UserProfile;
  /** Called with the stored value after a successful save, so the list row updates. */
  onSaved?: (profileId: number, department: string) => void;
}

/**
 * Department editor for customer-company users in the User Details modal.
 *
 * Saves through the managed-user update_profile endpoint with only
 * ``department`` in the body, so the user's notification switches are left as
 * they are. A stored department outside the list stays selectable as the
 * current value rather than being silently dropped. Read-only when the viewer
 * may not manage this user (``can_manage_user``).
 */
export function UserDepartmentField({ selectedUser, onSaved }: UserDepartmentFieldProps) {
  const [saved, setSaved] = useState(() => toSelectValue(selectedUser.department));
  const [draft, setDraft] = useState(saved);
  const [saving, setSaving] = useState(false);
  const canEdit = Boolean(selectedUser.can_manage_user);

  useEffect(() => {
    const value = toSelectValue(selectedUser.department);
    setSaved(value);
    setDraft(value);
  }, [selectedUser.id, selectedUser.department]);

  const save = async () => {
    const department = toStoredValue(draft);
    setSaving(true);
    try {
      const res = await coreAPI.updateManagedUserProfile(selectedUser.id, { department });
      const stored = res.data.user?.department ?? department;
      setSaved(toSelectValue(stored));
      onSaved?.(selectedUser.id, stored);
      toast.success(stored ? `Department set to ${departmentLabel(stored)}` : 'Department cleared');
    } catch (err: unknown) {
      console.error('Failed to update department:', err);
      toast.error(getApiErrorMessage(err, 'Failed to update department'));
    } finally {
      setSaving(false);
    }
  };

  const legacyValue = saved.startsWith(LEGACY_PREFIX) ? saved : null;

  return (
    <div>
      <div className="text-sm font-medium text-muted-foreground">Department</div>
      {canEdit ? (
        <div className="flex items-center gap-2 mt-1">
          <Select value={draft} onValueChange={setDraft} disabled={saving}>
            <SelectTrigger className="w-[200px]" aria-label="Department">
              <SelectValue placeholder="No department" />
            </SelectTrigger>
            <SelectContent>
              {legacyValue && (
                <SelectItem value={legacyValue}>
                  {toStoredValue(legacyValue)} (not in list)
                </SelectItem>
              )}
              {DEPARTMENTS.map((d) => (
                <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
              ))}
              <SelectItem value={NO_DEPARTMENT}>No department</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" disabled={saving || draft === saved} onClick={save}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      ) : (
        <p className="text-base mt-1">{departmentLabel(selectedUser.department) || '—'}</p>
      )}
      {canEdit && (
        <p className="text-xs text-muted-foreground mt-1">
          Changing the department does not change this user&apos;s notification settings.
        </p>
      )}
    </div>
  );
}

export default UserDepartmentField;
